// frontend/src/components/inventaire/VentesClientModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Divider,
  Alert
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Close as CloseIcon,
  Store as StoreIcon,
  Inventory as InventoryIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';

const VentesClientModal = ({ open, onClose, client, embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ventesData, setVentesData] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (open && client?.id) {
      fetchVentes();
    }
  }, [open, client]);

  const fetchVentes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`inventory/clients/${client.id}/ventes/`);
      setVentesData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement ventes:', err);
      setError('Impossible de charger l\'historique des ventes');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const renderVentesDepot = () => {
    const ventes = ventesData?.ventes_depot || [];
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2">
            Total: {ventes.length} vente(s)
          </Typography>
          <Chip 
            label={`Montant total: ${formatCurrency(ventes.reduce((sum, v) => sum + parseFloat(v.total || 0), 0))}`}
            color="primary"
            size="small"
          />
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Succursale</TableCell>
                <TableCell>Référence</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Caissier</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ventes.map((vente) => (
                <TableRow key={vente.id} hover>
                  <TableCell>{formatDate(vente.date_vente)}</TableCell>
                  <TableCell>{vente.succursale?.nom || '-'}</TableCell>
                  <TableCell>{vente.numero_facture || vente.reference || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(vente.total)}</TableCell>
                  <TableCell>{vente.caissier?.username || '-'}</TableCell>
                </TableRow>
              ))}
              {ventes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    Aucune vente dépôt
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderVentesInventory = () => {
    const ventes = ventesData?.ventes_inventory || [];
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2">
            Total: {ventes.length} vente(s)
          </Typography>
          <Chip 
            label={`Montant total: ${formatCurrency(ventes.reduce((sum, v) => sum + parseFloat(v.total || 0), 0))}`}
            color="primary"
            size="small"
          />
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Référence</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ventes.map((vente) => (
                <TableRow key={vente.id} hover>
                  <TableCell>{formatDate(vente.date_vente)}</TableCell>
                  <TableCell>{vente.numero_facture || vente.reference || '-'}</TableCell>
                  <TableCell align="right">{formatCurrency(vente.total)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={vente.statut || 'Terminé'} 
                      size="small"
                      color={vente.statut === 'brouillon' ? 'default' : 'success'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {ventes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    Aucune vente inventaire
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const content = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : ventesData ? (
        <Box>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab 
              icon={<StoreIcon />} 
              label={`Dépôt (${ventesData.total_ventes_depot || 0})`} 
              iconPosition="start"
            />
            <Tab 
              icon={<InventoryIcon />} 
              label={`Inventaire (${ventesData.total_ventes_inventory || 0})`} 
              iconPosition="start"
            />
          </Tabs>
          
          {tabValue === 0 && renderVentesDepot()}
          {tabValue === 1 && renderVentesInventory()}
        </Box>
      ) : null}
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6">
            Historique des ventes - {client?.nom_complet || `${client?.nom} ${client?.prenom || ''}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {content}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VentesClientModal;